import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import {
  invalidateTaskCaches,
  useTask,
} from '@/features/tasks/model/useTaskActions';
import { useRenewTask } from '@/features/tasks/mobile-calendar/hooks/useRenewTask';
import { completeTaskWithGuards } from '@/entities/task/api/completeTaskWithGuards';
import { completeTask, getTask as getTaskApi } from '@/entities/task/api/tasks';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useActiveTimerTask() {
  const {
    activeTaskId,
    lastTaskId,
    getTask,
    getCurrentElapsed,
    requestPause,
    startTask,
    tasks,
  } = useTaskTimerStore();

  const displayTaskId = activeTaskId ?? lastTaskId;
  const queryClient = useQueryClient();

  const timerTask = displayTaskId
    ? tasks.find((t) => t.taskId === displayTaskId)
    : undefined;

  const isLocalTask = displayTaskId?.includes('-');

  const realId = displayTaskId?.includes('-')
    ? displayTaskId.split('-')[1]
    : displayTaskId;

  const {
    data: apiTask,
    isLoading: apiLoading,
    isError: apiError,
  } = useTask(realId ?? '');

  const taskExists = isLocalTask
    ? Boolean(timerTask)
    : Boolean(apiTask) && !apiError;

  const hasTask = Boolean(displayTaskId) && taskExists;

  const isCompleted = apiTask?.status === 'done';

  const { renewTask, isLoading: isRenewing } = useRenewTask();

  const toggleTask = async () => {
    if (!realId || isCompleted) {
      return;
    }

    const timer = displayTaskId ? getTask(displayTaskId) : undefined;

    if (!timer || !timer.isRunning) {
      startTask(realId);
      return;
    }

    requestPause(realId);
  };

  const finishTask = async () => {
    if (!displayTaskId) {
      return;
    }

    const real = displayTaskId.includes('-')
      ? displayTaskId.split('-')[1]
      : displayTaskId;

    if (!real) {
      return;
    }

    try {
      const res = await getTaskApi(real);
      const fullTask = res.core;

      await completeTaskWithGuards({
        taskId: real,
        task: fullTask,
        completeTask: async (id) => {
          await completeTask(id);
        },
        beforeOpenModal: async () => {
          await invalidateTaskCaches(queryClient);
        },
      });
    } catch (e) {
      console.error('DesktopHeader finishTask error', e);

      try {
        await completeTaskWithGuards({
          taskId: real,
          task: null,
          completeTask: async (id) => {
            await completeTask(id);
          },
          beforeOpenModal: async () => {
            queryClient.invalidateQueries({ queryKey: ['task', real] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          },
        });
      } catch (e2) {
        console.error('DesktopHeader fallback finishTask error', e2);
        toast.error('Не удалось завершить задачу');
      }
    }
  };

  const renew = () => {
    if (!realId) {
      return;
    }
    renewTask(realId);
  };

  const title = hasTask
    ? isLocalTask
      ? (timerTask?.title ?? 'Без названия')
      : (apiTask?.title ?? 'Без названия')
    : '';

  const plannedMinutes = hasTask
    ? isLocalTask
      ? (timerTask?.plannedMinutes ?? 0)
      : apiTask?.timeEstimate
        ? Math.floor(apiTask.timeEstimate / 60)
        : 0
    : 0;

  const spentMinutes =
    hasTask && displayTaskId
      ? Math.floor(getCurrentElapsed(String(displayTaskId)) / 60000)
      : 0;

  const isRunning =
    hasTask && displayTaskId
      ? Boolean(getTask(String(displayTaskId))?.isRunning)
      : false;

  return {
    hasTask: !!displayTaskId,
    taskId: realId ?? null,

    apiLoading,
    title,
    plannedMinutes,
    spentMinutes,

    isRunning,
    isCompleted,
    isRenewing,

    toggleTask,
    finishTask,
    renew,
  };
}
